import { LabelDto } from 'business/models/CustomModels';
import { DrawingLayer } from 'business/models/DrawingLayer';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import {
  IScoresOfWellDropsDTO,
  LayerDTO,
  LayerDetailsDTO,
  Pagination,
  SearchDisplayFieldStatus,
  WellIngDTO,
} from 'business/models/Models';
import ExperimentService from 'business/services/ExperimentService';
import ScreenService from 'business/services/ScreenService';
import {
  createPlateLables,
  getDefaultCaptureProfileId,
  getDefaultRegion,
  getWellAndCanvasLabels,
} from 'components/commandpanel/Navigator/DRCIPWUtil';
import { IDropDownList } from 'components/customComponents/CommonComponents/RMDropDownList';
import { EReportTypes } from 'enums/EIconPanels';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import { getPlainObject } from 'transformers/utils';
import {
  DropDownListButtonsColor,
  convertToCsv,
  convertToTxt,
  downloadFilefromUrl,
  getFileURL,
  headerButtonsColor,
  headerIconSize,
  showAlertSnackBar,
} from 'utils/Global';
import useRockFetchGet from 'utils/RockFetch';
import SearchnFilterService from 'business/services/searchnFilterService';
import {
  EScoreGroupTypes,
  ESearchTargetNames,
  HitScoreName,
  SearchHeaderButtonParams,
} from './constants';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { Plate } from 'components/Experiment/constants';
import { DropDetails } from 'components/well/constants';
import { labelStyles } from './analytics/constants';
import EScoringGroup from 'enums/EScoringGroup';
import { ESelectedHitButton } from 'redux/reducers/headerReducer';

// search n filter
export const createNewSearchNode = async (
  parentTreeNodeId: number,
  defaultSearchPathId?: number,
) => {
  const snfService = new SearchnFilterService();
  return await snfService.createNewSearch(parentTreeNodeId, defaultSearchPathId);
};

export const stopPropagationAndPreventDefault = (e) => {
  e.stopPropagation();
  e.preventDefault();
};

export const exportSearchResults = async (
  performSearch,
  headerNames,
  isCsv,
  setIsLoading,
) => {
  setIsLoading(true);
  const searchResults = await performSearch(new Pagination(), false);
  const res = isCsv
    ? convertToCsv(searchResults?.searchRows, headerNames)
    : convertToTxt(searchResults?.searchRows, headerNames);
  const url = getFileURL(res, `text/${isCsv ? 'csv;charset=utf-8;' : 'plain'}`);
  downloadFilefromUrl(url, `searchResults.${isCsv ? 'csv' : 'txt'}`);
  setIsLoading(false);
};

export const searchHeaderButtons = (params: SearchHeaderButtonParams) => {
  const {
    displayFields,
    isResultTab,
    selectedTargetName,
    primarySelectedSearchRow,
    reportsList,
    isReportsDisabled,
    performSearch,
    setIsLoading,
    chartInstance,
    isChartTab,
    selectedChartName,
  } = params;
  const headerNames: string[] = displayFields
    ?.filter((field) => field.status === SearchDisplayFieldStatus.Display)
    .map((field) => {
      return field.columnHeader;
    });
  const btnList = [
    {
      iconProps: {
        subfolder: 'Reports',
        name: 'Report',
        fill: headerButtonsColor,
        ...headerIconSize,
      },
      title: 'Report',
      isDisabled: isReportsDisabled,
      color: headerButtonsColor,
      contentOrientation: 'Vertical',
      dropDownList: reportsList,
      //contentContainerClass: 'header-button-container',
      titleClass: 'header-button-title',
      showChevronIcon: true,
      chevronIconInitialAngle: 315,
      chevronIconPosition: 'end',
      contentContainerClass: 'header-button-container main-header-extended-button',
    },
    {
      iconProps: {
        name: 'ThreeDotMenu',
        fill: headerButtonsColor,
        ...headerIconSize,
      },
      title: 'Export',
      isDisabled: false,
      color: headerButtonsColor,
      contentOrientation: 'Vertical',
      contentContainerClass: 'header-button-container',
      titleClass: 'header-button-title',
      dropDownList: exportOptionButtons(performSearch, headerNames, setIsLoading),
    },
  ];
  return isResultTab
    ? [
        {
          title: 'OPEN',
          isDisabled: !primarySelectedSearchRow,
          contentContainerClass: 'tree-control-open-button',
          titleClass: 'tree-control-open-button-title',
          onClick: () =>
            openSelectedSearchTarget(selectedTargetName, primarySelectedSearchRow),
        },
        ...btnList,
      ]
    : isChartTab
    ? [
        {
          iconProps: {
            name: 'SaveChart',
            fill: headerButtonsColor,
            ...headerIconSize,
          },
          title: 'Save Chart',
          isDisabled: false,
          onClick: () =>
            exportChartJpeg(chartInstance.current, selectedChartName, setIsLoading),
          contentOrientation: 'Vertical',
          contentContainerClass: 'header-button-container',
          titleClass: 'header-button-title header-button-text-lg',
        },
      ]
    : btnList;
};

export const exportOptionButtons = (performSearch, headerNames, setIsLoading) => {
  return [
    {
      label: 'Excel',
      onClick: async () =>
        exportSearchResults(performSearch, headerNames, true, setIsLoading),
    },
    {
      label: 'Text',
      onClick: async () =>
        exportSearchResults(performSearch, headerNames, false, setIsLoading),
    },
  ];
};

export const getReportsButtonsList = (params: GetSnFReportsButtonsListParams) => {
  const { setReportType, selectedSearchTargetName } = params;
  const reportsList: IDropDownList[] = [];

  if (selectedSearchTargetName == 'Experiment') {
    reportsList.push({
      label: 'Experiment',
      iconProps: {
        subfolder: 'Reports',
        name: 'ExperimentReport',
        fill: DropDownListButtonsColor,
      },
      onClick: () => {
        setReportType(EReportTypes.ExperimentReport);
      },
      isDisabled: false,
    });
  }

  if (
    selectedSearchTargetName == 'Well Drop' ||
    selectedSearchTargetName == 'Drops by Score'
  ) {
    reportsList.push({
      label: 'Single Drop',
      iconProps: {
        subfolder: 'Reports',
        name: 'SingleDropReport',
        fill: DropDownListButtonsColor,
      },
      isDisabled: false,
      onClick: () => {
        setReportType(EReportTypes.SingleDropReport);
      },
    });
  }

  return reportsList;
};

export type GetSnFReportsButtonsListParams = {
  setReportType: (reportType: EReportTypes) => void;
  selectedSearchTargetName: any;
};

export const fetchLayers = async (
  expid: number,
  rockFetchGet,
): Promise<DrawingLayer[]> => {
  const expService = new ExperimentService();
  const tempLyrs = await expService.getLayers(expid);
  const layers: DrawingLayer[] = tempLyrs
    ? tempLyrs.map((tl) => LayerDTO.fromJS(tl))
    : new Array<DrawingLayer>();
  if (!layers) {
    return null;
  }
  const tempLyrDetails = await rockFetchGet(
    `/api/Experiment/layerdetails/${expid}`,
    'Get',
    undefined,
    true,
  );

  const layersDetails: LayerDetailsDTO[] = tempLyrDetails
    ? tempLyrDetails.map((tld) => LayerDetailsDTO.fromJS(tld))
    : new Array<LayerDetailsDTO>();
  if (layersDetails && layers) {
    layersDetails.forEach((ld) => {
      const tempLyr = layers.find((l) => l.id == ld.layerId);

      if (tempLyr) {
        tempLyr.layerDetail = ld;
      }
    });
  }
  return layers;
};

export const fetchExperimentAsync = async (
  expId: number,
  documentType: ETreeNodeTypes.Experiment | ETreeNodeTypes.Screen,
) => {
  const expService = new ExperimentService();
  const screenService = new ScreenService();
  const [rockFetchGet] = useRockFetchGet();

  const dataProviderService =
    documentType === ETreeNodeTypes.Experiment
      ? expService.getExperimentService
      : screenService.getScreenById;
  const [expData, layers, allPlateScores] = await Promise.all([
    (await dataProviderService(expId)) as ExperimentDtoClient,
    await fetchLayers(expId, rockFetchGet),
    await expService.getAllScoresOfExperimentPlateWiseByExpId(expId),
  ]);
  if (!expData) {
    return;
  }

  const plateLabels: LabelDto[] = await createPlateLables(
    expData.plateDto,
    expData.colCount,
  );
  const { wellLabels } = getWellAndCanvasLabels(
    expData.containerCols,
    expData.containerRows,
  );

  expData.wellLables = wellLabels;
  expData.plateLables = plateLabels;
  expData.layers = layers;
  await expService.loadAllPlatesIngredients(
    expData.plateDto,
    expData.containerMaxDropVolume,
    expData.layers,
    getDefaultRegion(expData),
    getDefaultCaptureProfileId(expData),
  );
  if (documentType === ETreeNodeTypes.Experiment) {
    expData.plateDto = expData.plateDto.map((plate) => {
      const plateId = plate.plateId;
      if (allPlateScores.allScores[plateId]) {
        return {
          ...plate,
          plateScores: allPlateScores.allScores[plateId],
        } as Plate;
      }
      return plate;
    });
  }
  expData.isDirty = false;
  expData.deletedPlateIds = [];
  expData.plateDto.sort((x, y) => x.plateNumber - y.plateNumber);
  const plainExpData = getPlainObject(expData);
  return plainExpData;
};
const openSelectedSearchTarget = (
  selectedTargetName: ESearchTargetNames,
  primarySelectedSearchRow: { [key: string]: any },
) => {
  const params = new URLSearchParams({
    plateId: primarySelectedSearchRow['Experiment Plate.ID'],
    wellNumber: primarySelectedSearchRow['Well.Number'],
  }).toString();

  const experimentIdKey = 'Experiment.ID';
  const _experimentIdKey = '__ExperimentID';
  const wellIdKey = 'Well.ID';
  const treeNodeIdKey = '__ID';
  const plateIdKey = 'Experiment Plate.ID';

  switch (selectedTargetName) {
    case ESearchTargetNames.Experiment:
    case ESearchTargetNames.WellDrop:
      if (primarySelectedSearchRow[experimentIdKey]) {
        window.open(
          `/exp/${primarySelectedSearchRow[experimentIdKey]}?${params}`,
          `/exp/${primarySelectedSearchRow[experimentIdKey]}`,
        );
      }
      break;
    case ESearchTargetNames.Screen:
      if (primarySelectedSearchRow[_experimentIdKey]) {
        window.open(
          `/screen/${primarySelectedSearchRow[_experimentIdKey]}`,
          `/screen/${primarySelectedSearchRow[_experimentIdKey]}`,
        );
      }
      break;
    case ESearchTargetNames.ProteinFormulation:
      if (primarySelectedSearchRow[treeNodeIdKey]) {
        window.open(
          `/protein/${primarySelectedSearchRow[treeNodeIdKey]}`,
          `/protein/${primarySelectedSearchRow[treeNodeIdKey]}`,
        );
      }
      break;
    case ESearchTargetNames.Project:
      if (primarySelectedSearchRow[treeNodeIdKey]) {
        window.open(
          `/project/${primarySelectedSearchRow[treeNodeIdKey]}`,
          `/project/${primarySelectedSearchRow[treeNodeIdKey]}`,
        );
      }
      break;
    case ESearchTargetNames.Seed:
      if (primarySelectedSearchRow[treeNodeIdKey]) {
        window.open(
          `/seed/${primarySelectedSearchRow[treeNodeIdKey]}`,
          `/seed/${primarySelectedSearchRow[treeNodeIdKey]}`,
        );
      }
      break;
    case ESearchTargetNames.Well:
    case ESearchTargetNames.WellIngredient:
    case ESearchTargetNames.DropsByScore:
    case ESearchTargetNames.Xtals:
      if (primarySelectedSearchRow[wellIdKey]) {
        new SearchnFilterService()
          .getExperimetnIdByWellId(primarySelectedSearchRow[wellIdKey])
          .then((experimentId) => {
            window.open(`/exp/${experimentId}`, `/exp/${experimentId}`);
          });
      }
      break;
    case ESearchTargetNames.ScreenHits:
      if (primarySelectedSearchRow[treeNodeIdKey]) {
        new SearchnFilterService()
          .getExperimetnIdByTreeNodeId(primarySelectedSearchRow[treeNodeIdKey])
          .then((experimentId) => {
            window.open(`/screen/${experimentId}`, `/screen/${experimentId}`);
          });
      }
      break;
    case ESearchTargetNames.RegionAnnotation:
      if (primarySelectedSearchRow[treeNodeIdKey]) {
        new SearchnFilterService()
          .getExperimetnIdByTreeNodeId(primarySelectedSearchRow[treeNodeIdKey])
          .then((experimentId) => {
            window.open(`/exp/${experimentId}`, `/exp/${experimentId}`);
          });
      }
      break;
    case ESearchTargetNames.ExperimentPlate:
      if (primarySelectedSearchRow[plateIdKey]) {
        new SearchnFilterService()
          .getExperimetnIdByPlateId(primarySelectedSearchRow[plateIdKey])
          .then((experimentId) => {
            window.open(`/exp/${experimentId}`, `/exp/${experimentId}`);
          });
      }
      break;
    default:
      showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, 'Could not open target');
      break;
  }
};

export function getTreeNodePath(nodes, pTreeNodeId, currentPath = []) {
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].id === pTreeNodeId) {
      currentPath.push(nodes[i].name); // Add the current node id to the path
      return currentPath; // Return the current path when the node is found
    } else if (nodes[i].children) {
      const childPath = currentPath.slice(); // Create a copy of the current path
      childPath.push(nodes[i].name); // Add the current node id to the path
      const result = getTreeNodePath(nodes[i].children, pTreeNodeId, childPath);
      if (result) {
        return result; // Return the result if the node is found in the child subtree
      }
    }
  }
  return null; // Return null if the node is not found in the tree
}

export const getSnfWellKey = (dropDetails: DropDetails) => {
  return `${dropDetails.rowNumber}-${dropDetails.regionID}-${dropDetails.cpID}-${dropDetails.imageBatchId}`;
};

export const splitOnFirstDot = (str) => {
  const index = str.indexOf('.');
  return index !== -1 ? [str.substring(0, index), str.substring(index + 1)] : [str, '']; // If no dot is found, return the whole string.
};

const exportChartJpeg = (
  chartInstance,
  chartName: string,
  setIsLoading: (e: boolean) => void,
) => {
  setIsLoading(true);
  const options = chartInstance.getOption();
  chartInstance.setOption({
    ...options,
    legend: {
      ...options.legend,
      show: true,
    },
    dataZoom: options.dataZoom.map((d) => ({
      ...d,
      show: false,
    })),
  });
  const dataURL = chartInstance.getDataURL({
    type: 'jpeg', // or 'jpg'
    pixelRatio: 2,
    backgroundColor: '#fff', // Important for JPG (no transparency)
  });

  downloadFilefromUrl(dataURL, 'DataAnalysis - ' + chartName + '.jpg');
  chartInstance.setOption({
    ...options,
    legend: {
      ...options.legend,
      show: false,
    },
  });
  setIsLoading(false);
};

export const getFormattedSeriesData = (seriesData, showLabels) => {
  return seriesData.map((s) => ({
    ...s,
    label: {
      show: showLabels,
      formatter: (param) => (param.value > 0 ? `{value|${param.value}}` : ''),
      rich: labelStyles,
    },
  }));
};

export const isNumericDataChart = (
  availableCharts,
  selectedChartId,
  searchTargetFields,
) => {
  const chart = availableCharts?.find((c) => c.id === selectedChartId);
  if (chart) {
    const [tableName, colName] = splitOnFirstDot(chart.xaxis);
    const searchFields = searchTargetFields.find((t) => t.name === tableName);
    const searchFieldClass = searchFields?.searchFields?.find(
      (f) => f.displayName === colName,
    )?.fieldClass;
    return searchFieldClass === 'Integer' || searchFieldClass === 'Number';
  }
  return false;
};

export const filterScores = (
  scoreGroup: number,
  wellScores: IScoresOfWellDropsDTO[],
  hideAutoScores: boolean,
) => {
  let filteredScores = hideAutoScores
    ? wellScores.filter((score) => !score.isAutoScore)
    : wellScores;
  switch (scoreGroup) {
    case EScoringGroup.All:
      filteredScores = filteredScores;
      break;
    case EScoringGroup.Auto:
      filteredScores = filteredScores.filter((fscores) => fscores.isAutoScore == true);
      break;
    case EScoringGroup.Manual:
      filteredScores = filteredScores.filter((fscores) => fscores.isAutoScore == false);
      break;
    default:
      filteredScores = filteredScores.filter(
        (fscores) => fscores.wellDropScoreGroupId === scoreGroup,
      );
      break;
  }
  return filteredScores;
};

export const checkIsNonHitDrop = (
  dropScores: IScoresOfWellDropsDTO[],
  scoreGroupType: string,
  scoreGroup: number,
  hitButton: ESelectedHitButton,
) => {
  if (!dropScores) {
    return true;
  }
  const checkCondition = (batchScore: IScoresOfWellDropsDTO) => {
    const hit = batchScore.hit || batchScore.scoreName === HitScoreName;
    const groupMatch = scoreGroup == 0 || batchScore.wellDropScoreGroupId == scoreGroup;

    const condtionsMap = {
      [EScoreGroupTypes.All]: hit && groupMatch,
      [EScoreGroupTypes.Auto]: hit && groupMatch && batchScore.isAutoScore,
      [EScoreGroupTypes.Manual]: batchScore.hit && groupMatch && !batchScore.isAutoScore,
    };
    return condtionsMap[scoreGroupType] ?? false;
  };

  if (hitButton === ESelectedHitButton.None) {
    return false;
  } else if (hitButton === ESelectedHitButton.AnyHits) {
    return !dropScores.some(checkCondition);
  } else if (hitButton === ESelectedHitButton.AllHits) {
    return !dropScores.every(checkCondition);
  }
};

export const getLabelsConfig = (width, labelCount) => {
  const horizontalSpaceNeeded = 45; // px needed per horizontal label
  const verticalSpaceNeeded = 15; // px needed per vertical label

  // Space per label if ALL shown
  const pxPerLabel = width / labelCount;

  let rotationAngle = 0;
  let interval = 0; // show all

  if (pxPerLabel < horizontalSpaceNeeded) {
    // Scale rotation if space is tight
    const shortageRatio = horizontalSpaceNeeded / Math.max(pxPerLabel, 1);
    rotationAngle = Math.min(90, Math.round(shortageRatio * 30));
  }

  // If even vertical (90°) labels don’t fit, start skipping
  if (rotationAngle === 90) {
    if (pxPerLabel < verticalSpaceNeeded) {
      // Calculate how many to skip
      interval = Math.ceil((verticalSpaceNeeded * labelCount) / width) - 1;
    }
  }
  return { interval, rotationAngle };
};

export const getAllWellsWithMatchingIngredientStocks = (
  wellDrops: WellIngDTO[],
  ingredientStockId: number,
) => {
  const matchingWells: number[] = [];

  wellDrops.forEach((wd, index) => {
    const wellStockIds = wd.wellLayerList.flatMap((wl) =>
      wl.wellLayerIngredientList.map((ing) => ing.ingredientStockId),
    );

    const hasMatch = wellStockIds.some((id) => id === ingredientStockId);

    if (hasMatch) {
      matchingWells.push(index + 1);
    }
  });

  return matchingWells;
};
